/**
 * Validation aligned with main project (frontendrealestate) form.validation.ts.
 * Returns error keys that match i18n errors.* so the widget can t('errors.' + key).
 */
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const nameRegex = /^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s'-]*[A-Za-zÀ-ÿ]$/

export function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

export function isValidEmail(email: string): boolean {
  return emailRegex.test(email.trim())
}

export function isValidCountryCode(country: string): boolean {
  const c = country.trim()
  return /^[A-Z]{2}$/.test(c)
}

/** Phone: 8–15 digits (main project uses value.length 8–15 on raw input; we count digits). */
export function isValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '')
  return digits.length >= 8 && digits.length <= 15
}

/** Returns main-project error key or null. */
export function getEmailError(email: string): 'required' | 'invalid_email' | null {
  const t = email.trim()
  if (!t) return 'required'
  if (!emailRegex.test(t)) return 'invalid_email'
  return null
}

/** Returns main-project error key or null. */
export function getNameError(name: string): string | null {
  const n = normalizeWhitespace(name)
  if (n.length === 0) return 'required'
  if (n.length < 2) return 'name_too_short'
  if (n.length > 30) return 'name_too_long'
  if (!nameRegex.test(n)) return 'invalid_name_format'
  if (/(['-])\1|\s{2,}/.test(n)) return 'invalid_name_special_chars'
  if (/\d/.test(n)) return 'invalid_name_numbers'
  return null
}

/** Returns main-project error key or null. */
export function getCountryError(country: string): 'select_country' | null {
  const c = country.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(c)) return 'select_country'
  return null
}

type PhoneValidationOptions = {
  requireCountryCode?: boolean
}

/** Returns main-project error key or null. */
export function getPhoneError(
  phone: string,
  options: PhoneValidationOptions = {}
): 'enter_phone' | 'valid_phone' | 'phone_country_code_required' | null {
  const trimmed = phone.trim()
  if (!trimmed) return 'enter_phone'
  if (options.requireCountryCode && !trimmed.startsWith('+')) {
    return 'phone_country_code_required'
  }
  const digits = trimmed.replace(/\D/g, '')
  if (digits.length < 8) return 'valid_phone'
  if (digits.length > 15) return 'valid_phone'
  return null
}

/** Returns main-project error key or null. */
export function getMessageError(message: string): 'message_too_long' | null {
  if (message.length > 500) return 'message_too_long'
  return null
}

export function isValidName(name: string): boolean {
  return getNameError(name) === null
}
